import { route } from '@/ziggy-shim';
import { api, apiConfig, CastValue } from './LaravelClient';

export default {
  async index() {
    return await api(route('api.v1.projects.index'), 'get');
  },

  async search(data: ApiRequests['api.v1.projects.search']) {
    return await api(route('api.v1.projects.search'), 'get', {
      data,
      casts: {
        search: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        contract_start_date: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        contract_end_date: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        financial_year: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        status: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async show(id: Id) {
    return await api(route('api.v1.projects.show', id), 'get');
  },

  async create(data: ApiRequests['api.v1.projects.store']) {
    return await api(route('api.v1.projects.store'), 'post', {
      data,
      casts: {
        activities: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        areas: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        elements: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        wbs_codes: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async companyCreate(companyId: Id, data: ApiRequests['api.v1.company.projects.store']) {
    return await api(route('api.v1.company.projects.store', companyId), 'post', {
      data,
    });
  },

  async update(id: Id, data: ApiRequests['api.v1.projects.update']) {
    return await api(route('api.v1.projects.update', id), 'patch', {
      data,
      casts: {
        reference: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        default_dashboard: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        description: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        business_role: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async updateHeadContractSettings(id: Id, data: ApiRequests['api.v1.projects.head-contract-settings']) {
    return await api(route('api.v1.projects.head-contract-settings', id), 'patch', {
      data,
      casts: {
        client_details: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        financier_details: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        hired_company_details: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        site_details: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        apply_tax: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        certificate_clause: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        claim_clause: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        final_account_clause: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async updateSubcontractSettings(id: Id, data: ApiRequests['api.v1.projects.subcontract-settings']) {
    return await api(route('api.v1.projects.subcontract-settings', id), 'patch', {
      data,
    });
  },

  async delete(id: Id) {
    return await api(route('api.v1.projects.destroy', id), 'delete');
  },

  async wbsCodeAverage(id: Id, data: ApiRequests['api.v1.projects.wbs_code.average']) {
    return await api(route('api.v1.projects.wbs_code.average', id), 'get', {
      data,
    });
  },

  async utilisationReport(id: Id, data: ApiRequests['api.v1.projects.plant-utilisation']) {
    return await api(route('api.v1.projects.plant-utilisation', id), 'get', {
      data,
    });
  },

  async plantUtilisationGraph(
    id: Id,
    data: ApiRequests['api.v1.projects.plant-utilisation-metrics'] = { date: undefined },
  ) {
    return await api(route('api.v1.projects.plant-utilisation-metrics', id), 'get', {
      data,
    });
  },

  async selfPerformMetrics(id: Id, data: ApiRequests['api.v1.projects.self-perform-metrics'] = { date: undefined }) {
    return await api(route('api.v1.projects.self-perform-metrics', id), 'get', {
      data,
    });
  },

  async costAggregationSummary(id: Id) {
    return await api(route('api.v1.projects.cost-aggregation', id), 'get');
  },

  async weeklyPerformanceGraph(id: Id) {
    return await api(route('api.v1.projects.weekly-performance-metrics', id), 'get');
  },

  async clientMetrics(id: Id) {
    return await api(route('api.v1.projects.client-metrics', id), 'get');
  },

  async seedDemo(data: ApiRequests['api.v1.admin.projects.seed-demo']) {
    return await api(route('api.v1.admin.projects.seed-demo'), 'post', {
      data,
      casts: {
        company_id: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        name: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async adminIndex(page = 1) {
    return await apiConfig(route('api.v1.admin.projects.index'), 'get', {
      params: {
        page,
      },
    });
  },

  async lockBoq(id: Id) {
    return await api(route('api.v1.projects.lock-boq', id), 'post');
  },

  async unlockBoq(id: Id) {
    return await api(route('api.v1.projects.unlock-boq', id), 'post');
  },

  async archive(id: Id) {
    return await api(route('api.v1.projects.archive', id), 'post');
  },

  async unarchive(id: Id) {
    return await api(route('api.v1.projects.unarchive', id), 'post');
  },

  coverPage: {
    async store(id: Id, data: ApiRequests['api.v1.projects.cover-page.store'] | FormData) {
      return await api(route('api.v1.projects.cover-page.store', id), 'post', {
        data,
      });
    },

    async show(id: Id) {
      return await api(route('api.v1.projects.cover-page.show', id), 'get');
    },
  },

  documents: {
    async store(id: Id, data: ApiRequests['api.v1.projects.documents.store'] | FormData) {
      return await api(route('api.v1.projects.documents.store', id), 'post', {
        data,
      });
    },

    async show(id: Id) {
      return await api(route('api.v1.projects.documents.show', id), 'get');
    },
  },

  reports: {
    async index(id: Id) {
      return await api(route('api.v1.projects.monthly-reports.index', id), 'get');
    },

    async generate(id: Id, data: ApiRequests['api.v1.projects.monthly-report']) {
      return await api(route('api.v1.projects.monthly-report', id), 'post', {
        data,
        casts: {
          month: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },
  },

  commentary: {
    async index(id: Id) {
      return await api(route('api.v1.projects.commentary.index', id), 'get');
    },

    async store(id: Id, data: ApiRequests['api.v1.projects.commentary.store']) {
      return await api(route('api.v1.projects.commentary.store', id), 'post', {
        data,
      });
    },

    async update(projectId: Id, commentId: Id, data: ApiRequests['api.v1.projects.commentary.update']) {
      return await api(route('api.v1.projects.commentary.update', [projectId, commentId]), 'patch', {
        data,
      });
    },

    async show(projectId: Id, commentId: Id) {
      return await api(route('api.v1.projects.commentary.show', [projectId, commentId]), 'get');
    },
  },

  admin: {
    async search(data: ApiRequests['api.v1.admin.projects.search']) {
      return await api(route('api.v1.admin.projects.search'), 'get', {
        data,
        casts: {
          name: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async update(id: Id, data: ApiRequests['api.v1.admin.projects.update']) {
      return await api(route('api.v1.admin.projects.update', id), 'patch', {
        data,
        casts: {
          reference: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
          default_dashboard: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async forceDelete(id: Id) {
      return await api(route('api.v1.admin.projects.force-delete', id), 'delete');
    },

    async restore(id: Id) {
      return await api(route('api.v1.admin.projects.restore', id), 'patch');
    },
  },

  users: {
    async search(id: Id, data: ApiRequests['api.v1.projects.users.search']) {
      return await api(route('api.v1.projects.users.search', id), 'get', {
        data,
        casts: {
          name: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },
  },
};
